import { FilterContext, FilterFn, FilterSpec } from '../type';

export class FilterBuilder {
  private _filters: FilterSpec[] = [];

  private regExp = {
    num: /[^0-9]/g,
    kor: /[^ㄱ-ㅎㅏ-ㅣ가-힝\,\.\-\u318D\u119E\u11A2\u2022\u2025a\u00B7\uFE55\u318d\u11a2\u119e]/g,
    // korcomp: /[^ㄱ-ㅎㅏ-ㅣ가-힣\,\.\-]/g, // 완성형 한글 체크 함.
    alpha: /[^a-zA-Z]/g,
    alphanum: /[^a-zA-Z0-9]/g,
    upper: /[^A-Z]/g,
    lower: /[^a-z]/g,
    phone: /[^0-9\-]/g,
    email: /[^a-zA-Z0-9\@\-\_\.]/g,
    domain: /[^a-zA-Z0-9\:\/\.]/gi,
    date: /[^0-9\-]/g,
    month: /[^0-9\-]/g,
    float: /[^0-9\.]/g,
    money: /[^0-9\,\.]/g,
    accno: /[^0-9\-]/g,
    special: /[\~\!\@\#\$\%\^\&\*\(\)\_\+\`\-\=\:\"\;\'\{\}\|\[\]\\\<\>\?\,\.\/]/g,
    space: /[^\s]/g,
    id: /[^a-zA-Z0-9\_]/g,
    //emoji: /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu,
    emoji: /[\p{Extended_Pictographic}\u{FE0F}\u{200D}]/gu,
  } as const;

  /**
   * 필터 추가
   * @param name
   * @param fn
   * @returns
   */
  private add(name: string, fn: FilterFn) {
    this._filters.push({ name, fn });
    return this;
  }

  /**
   * trim 처리
   * @returns
   */
  trim() {
    return this.add('trim', (v) => v.trim());
  }

  /**
   * 한글만
   */
  kor() {
    return this.add('kor', (v) => v.replace(this.regExp.kor, ''));
  }

  /**
   * 완성형 한글만
   * @returns
   */
  // korcomp() {
  //   return this.add('korcomp', (v) => v.replace(this.regExp.korcomp, ''));
  // }

  /**
   * 영문만
   * @returns
   */
  alpha() {
    return this.add('alpha', (v) => v.replace(this.regExp.alpha, ''));
  }

  /**
   * 영문만
   * @returns
   */
  alphanum() {
    return this.add('alphanum', (v) => v.replace(this.regExp.alphanum, ''));
  }

  /**
   * 영문 소문자
   * @returns
   */
  lower() {
    return this.add('lower', (v) => v.replace(this.regExp.lower, ''));
  }

  /**
   * 영문 대문자
   * @returns
   */
  upper() {
    return this.add('upper', (v) => v.replace(this.regExp.upper, ''));
  }

  /**
   * 전화번호형식
   * @returns
   */
  phone() {
    return this.add('phone', (v) => v.replace(this.regExp.phone, ''));
  }

  /**
   * 이메일형식
   * @returns
   */
  email() {
    return this.add('email', (v) => v.replace(this.regExp.email, ''));
  }

  /**
   * 도메인형색
   * @returns
   */
  domain() {
    return this.add('domain', (v) => v.replace(this.regExp.domain, ''));
  }

  /**
   * 숫자만
   * @returns
   */
  number() {
    return this.add('number', (v) => v.replace(this.regExp.num, ''));
  }

  /**
   * 패턴
   * @param regex
   * @returns
   */
  pattern(regex: RegExp) {
    return this.add('pattern', (v) => {
      const matches = v.match(regex);
      return matches ? matches.join('') : '';
    });
  }

  /**
   * 이모지 필터링
   * @returns
   */
  noEmoji() {
    return this.add('noEmoji', (v) => {
      const matches = v.match(this.regExp.emoji);
      return matches ? v.replace(this.regExp.emoji, '') : v;
    });
  }

  max(maxLength: number) {
    return this.add('max', (v) => {
      return v.slice(0, maxLength);
    });
  }

  /**
   * 커스텀 필터
   * @param name
   * @param fn
   * @returns
   */
  custom(name: string, fn: FilterFn) {
    return this.add(name, fn);
  }

  build() {
    const chain = [...this._filters];
    return (input: string, ctx: FilterContext) => {
      let out: string | undefined = input;
      for (const f of chain) {
        if (!f) {
          continue;
        }
        out = f.fn(out ?? '', ctx);
      }
      return out;
    };
  }
}

export const filterBuilder = () => new FilterBuilder();
